import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    CircularProgress, 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const MenuPage = () => {
    useAuthCheck();
    const { menuId } = useParams();    
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [menu, setMenu] = useState({});

    const initialValues = {
        text: menu.text || "",
        path: menu.path || "",
        group: menu.group || "",
        icon: menu.icon || "",
        sortOrder: menu.sortOrder ?? 0,
        parent: menu.parent || "",
    };
    
    useEffect(() => {     
        const fetchMenu = async () => {
            try {
                setLoading(true);

                if (menuId) {
                    const res = await api.get(`/menus/${menuId}`);                    
                    setMenu(res.data);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        fetchMenu();
    }, [menuId]); 
    
    const checkoutSchema = yup.object().shape({
        text: yup.string().required("Menu text is required"),
        group: yup.string().required("Group is required"),
        path: yup.string().nullable(),
        icon: yup.string().nullable(),
        sortOrder: yup.number().min(0),
    });


    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = menuId ? 'PUT' : 'POST';
            const url = menuId 
                ? `/menus/update/${menuId}` 
                : '/menus/new';

            await api({
                method,
                url,
                data: {
                    text: values.text,
                    path: values.path || null,
                    group: values.group,
                    icon: values.icon || null,
                    sortOrder: values.sortOrder,
                    parent: values.parent || null,
                },
            });

            toast.success(menuId 
                ? 'Menu updated successfully.' 
                : 'Menu created successfully.'
            );
            
            // onSubmitProps.resetForm();
            navigate("/menus");
        } catch (error) {
            setError(error);
            toast.error(error?.message || "Failed to submit menu!");
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Menu"
                    subtitle={`${loading ? 'Loading...' : menuId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/menus")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                >
                                    <TextField
                                        label="Text"
                                        name="text"
                                        required
                                        fullWidth
                                        variant="filled"
                                        value={values.text}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.text && !!errors.text}
                                        helperText={touched.text && errors.text}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                    />

                                    <TextField
                                        label="Path"
                                        name="path"
                                        fullWidth
                                        variant="filled"
                                        value={values.path}
                                        onChange={handleChange}
                                        error={touched.path && !!errors.path}
                                        helperText={touched.path && errors.path}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                    />

                                    <TextField
                                        label="Group"
                                        name="group"
                                        required
                                        fullWidth
                                        variant="filled"
                                        value={values.group}
                                        onChange={handleChange}
                                        error={touched.group && !!errors.group}
                                        helperText={touched.group && errors.group}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                    />

                                    <TextField
                                        label="Icon Name"
                                        name="icon"
                                        fullWidth
                                        variant="filled"
                                        placeholder="DashboardIcon"
                                        value={values.icon}
                                        onChange={handleChange}
                                        error={touched.icon && !!errors.icon}
                                        helperText={touched.icon && errors.icon}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                    />

                                    <TextField
                                        label="Sort Order"
                                        name="sortOrder"
                                        type="number"
                                        fullWidth
                                        variant="filled"
                                        value={values.sortOrder}
                                        onChange={handleChange}
                                        error={touched.sortOrder && !!errors.sortOrder}
                                        helperText={touched.sortOrder && errors.sortOrder}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                    />

                                    <TextField
                                        label="Parent Menu ID"
                                        name="parent"
                                        fullWidth
                                        variant="filled"
                                        value={values.parent}
                                        onChange={handleChange}
                                        error={touched.parent && !!errors.parent}
                                        helperText={touched.parent && errors.parent}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {menuId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default MenuPage;
